import React, { useCallback, useEffect, useState } from 'react';
import {
  ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View,
} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';
import ActionLoader from '../../components/ActionLoader';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRoute } from '@react-navigation/native';
import { useTheme } from '../../context/ThemeContext';
import { getThemeColors } from '../../utils/themeStyles';
import { useAuth } from '../../context/AuthContext';
import { Platform } from 'react-native';
import {
  orgApi, OrgGroup, OrgMember, OrgRole, OrgInvitation, OrgCompanyMission, LicenseUsage, LicenseCart,
} from '../../api/org';
import { useT } from '../../i18n/useT';
import type { MessageKey } from '../../i18n';

// ⚠️ THE LABEL IS A KEY, NOT A STRING. Declared at module level, the tab list is built ONCE — before
// any hook has run — so a translated string here would freeze the language picked on first import.
// The key is resolved inside the component, on every render, and therefore follows `?lang=`.
type Tab = 'groups' | 'members' | 'invitations' | 'subscription';
const TABS: { key: Tab; labelKey: MessageKey }[] = [
  // The order of the WORK, not a technical order: you invite, you activate, you file people into
  // groups, and you only look at licences when one is missing.
  { key: 'invitations', labelKey: 'companyDetail.tab.invitations' },
  { key: 'members', labelKey: 'companyDetail.tab.members' },
  { key: 'groups', labelKey: 'companyDetail.tab.groups' },
  { key: 'subscription', labelKey: 'companyDetail.tab.licenses' },
];

export default function CompanyDetailScreen() {
  const t = useT();
  const route = useRoute<any>();
  const companyId: number = route.params?.companyId;
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  const { getAccessToken } = useAuth();
  const token = getAccessToken();

  const [tab, setTab] = useState<Tab>((route.params?.tab as Tab) || 'invitations');
  const [roles, setRoles] = useState<OrgRole[]>([]);
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [groups, setGroups] = useState<OrgGroup[]>([]);
  const [catalog, setCatalog] = useState<OrgCompanyMission[]>([]);
  const [invitations, setInvitations] = useState<OrgInvitation[]>([]);
  const [subscription, setSubscription] = useState<any>(null);
  const [usage, setUsage] = useState<LicenseUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const loadCore = useCallback(async () => {
    try {
      const [r, m, g, c] = await Promise.all([
        orgApi.roles(token, companyId), orgApi.members(token, companyId),
        orgApi.groups(token, companyId), orgApi.catalog(token, companyId),
      ]);
      setRoles(r.roles ?? []); setMembers(m.members ?? []); setUsage(m.usage ?? null);
      setGroups(g.groups ?? []); setCatalog(c.missions ?? []);
    } catch { setErr(t('company.loadFailed')); }
    finally { setLoading(false); }
  }, [token, companyId, t]);

  useEffect(() => { loadCore(); }, [loadCore]);
  useEffect(() => {
    // ⚠️ MEMBERS ARE REFETCHED TOO, AND THIS IS A DEFECT WE LIVED THROUGH. `loadCore` only runs
    // ON MOUNT: an invitee who accepted while the screen was open never appeared under "Members".
    // They were in the database all along, with status `accepted` — the product was right, the
    // screen was showing a stale snapshot. The two other tabs already refetched; this one had
    // simply been forgotten.
    if (tab === 'members') orgApi.members(token, companyId).then(r => { setMembers(r.members ?? []); setUsage(r.usage ?? null); }).catch(() => {});
    if (tab === 'invitations') orgApi.companyInvitations(token, companyId).then(r => setInvitations(r.invitations ?? [])).catch(() => {});
    if (tab === 'subscription') orgApi.subscription(token, companyId).then(r => { setSubscription(r.subscription); setUsage(r.usage ?? null); }).catch(() => {});
  }, [tab, token, companyId]);

  return (
    <SafeAreaView style={[s.container, { backgroundColor: colors.background }]}>
      <View style={[s.tabs, { backgroundColor: colors.surface }]}>
        {/* `tb`, not `t` — the loop variable must NOT shadow the translate function. */}
        {TABS.map(tb => (
          <TouchableOpacity key={tb.key} onPress={() => setTab(tb.key)}
            style={[s.tab, tab === tb.key && { backgroundColor: colors.tabActiveBackground }]}>
            <Text style={{ color: tab === tb.key ? colors.tabActive : colors.tabInactive, fontWeight: '600', fontSize: 13 }}>{t(tb.labelKey)}</Text>
          </TouchableOpacity>
        ))}
      </View>
      {loading ? <View style={{ marginTop: 24 }}><ActionLoader label={t('companyDetail.loading')} /></View> : (
        <ScrollView contentContainerStyle={s.scroll}>
          {err && <Text style={{ color: colors.error, marginBottom: 10 }}>{err}</Text>}
          {tab === 'groups' && (
            <GroupsTab c={colors} token={token} companyId={companyId} groups={groups} roles={roles}
              members={members} catalog={catalog} reload={loadCore} />
          )}
          {tab === 'members' && (
            <MembersTab c={colors} token={token} companyId={companyId} members={members} roles={roles} usage={usage}
              reload={loadCore} goBuy={() => setTab('subscription')} />
          )}
          {tab === 'invitations' && (
            <InvitationsTab c={colors} token={token} companyId={companyId} roles={roles} groups={groups}
              invitations={invitations}
              reload={() => orgApi.companyInvitations(token, companyId).then(r => setInvitations(r.invitations ?? []))} />
          )}
          {tab === 'subscription' && (
            <SubscriptionTab c={colors} token={token} companyId={companyId} subscription={subscription} usage={usage}
              onSaved={(sub: any, u: LicenseUsage | null) => { setSubscription(sub); if (u) setUsage(u); }}
              onUsage={(u: LicenseUsage) => setUsage(u)} />
          )}
        </ScrollView>
      )}
    </SafeAreaView>
  );
}

// --- Multi-select chips ----------------------------------------------------
function Chips({ c, options, selected, onToggle }: {
  c: any; options: { id: number; label: string }[]; selected: Set<number>; onToggle: (id: number) => void;
}) {
  return (
    <View style={s.chipsRow}>
      {options.map(o => {
        const on = selected.has(o.id);
        return (
          <TouchableOpacity key={o.id} onPress={() => onToggle(o.id)}
            style={[s.chip, { borderColor: on ? c.primary : c.border, backgroundColor: on ? c.primary + '22' : 'transparent' }]}>
            <Text style={{ fontSize: 12, color: c.textPrimary }}>{o.label}</Text>
          </TouchableOpacity>
        );
      })}
      {options.length === 0 && <Text style={{ fontSize: 12, color: c.textTertiary }}>—</Text>}
    </View>
  );
}

// --- Groups tab ------------------------------------------------------------
function GroupsTab({ c, token, companyId, groups, roles, members, catalog, reload }: any) {
  const t = useT();
  const [name, setName] = useState('');
  const [expanded, setExpanded] = useState<number | null>(null);
  const [detail, setDetail] = useState<OrgGroup | null>(null);
  const [rSel, setRSel] = useState<Set<number>>(new Set());
  const [mSel, setMSel] = useState<Set<number>>(new Set());
  const [misSel, setMisSel] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!name.trim()) return;
    await orgApi.createGroup(token, companyId, { name: name.trim() });
    setName(''); await reload();
  };
  const open = async (g: OrgGroup) => {
    if (expanded === g.group_id) { setExpanded(null); return; }
    setExpanded(g.group_id);
    const d = await orgApi.group(token, companyId, g.group_id);
    setDetail(d); setRSel(new Set(d.role_ids ?? [])); setMSel(new Set(d.member_ids ?? [])); setMisSel(new Set(d.mission_ids ?? []));
  };
  const toggle = (set: any, val: number) => set((prev: Set<number>) => { const n = new Set(prev); n.has(val) ? n.delete(val) : n.add(val); return n; });
  const save = async (g: OrgGroup) => {
    setBusy(true);
    try {
      await orgApi.setGroupRoles(token, companyId, g.group_id, [...rSel]);
      await orgApi.setGroupUsers(token, companyId, g.group_id, [...mSel]);
      await orgApi.setGroupMissions(token, companyId, g.group_id, [...misSel]);
      setExpanded(null); await reload();
    } finally { setBusy(false); }
  };
  const remove = async (g: OrgGroup) => { await orgApi.deleteGroup(token, companyId, g.group_id); setExpanded(null); await reload(); };

  return (
    <View>
      <View style={s.inlineForm}>
        <TextInput placeholder={t('companyDetail.group.name')} value={name} onChangeText={setName} placeholderTextColor={c.inputPlaceholder}
          style={[s.input, { flex: 1, backgroundColor: c.inputBackground, borderColor: c.inputBorder, color: c.inputText }]} />
        <TouchableOpacity onPress={create} style={[s.addBtn, { backgroundColor: c.primary }]}>
          <Ionicons name="add" size={18} color="#FFF" />
        </TouchableOpacity>
      </View>
      {groups.map((g: OrgGroup) => (
        <View key={g.group_id} style={[s.card, { backgroundColor: c.cardBackground }]}>
          <TouchableOpacity style={s.cardHead} onPress={() => open(g)}>
            <Ionicons name="people" size={18} color={c.primary} />
            <View style={{ flex: 1 }}>
              <Text style={[s.cardTitle, { color: c.textPrimary }]}>{g.name}</Text>
              <Text style={{ fontSize: 12, color: c.textSecondary }}>
                {t('companyDetail.group.counts', { roles: g.role_count ?? 0, members: g.member_count ?? 0 })}
              </Text>
            </View>
            <Ionicons name={expanded === g.group_id ? 'chevron-up' : 'chevron-down'} size={18} color={c.textTertiary} />
          </TouchableOpacity>
          {expanded === g.group_id && detail && (
            <View style={{ marginTop: 10, gap: 10 }}>
              <Text style={[s.lbl, { color: c.textSecondary }]}>{t('companyDetail.group.roles')}</Text>
              <Chips c={c} selected={rSel} onToggle={(id) => toggle(setRSel, id)}
                options={roles.map((r: OrgRole) => ({ id: r.roleid, label: r.name }))} />
              <Text style={[s.lbl, { color: c.textSecondary }]}>{t('companyDetail.group.members')}</Text>
              <Chips c={c} selected={mSel} onToggle={(id) => toggle(setMSel, id)}
                options={members.map((m: OrgMember) => ({ id: m.app_user_id, label: m.email }))} />
              <Text style={[s.lbl, { color: c.textSecondary }]}>{t('companyDetail.group.missions')}</Text>
              <Chips c={c} selected={misSel} onToggle={(id) => toggle(setMisSel, id)}
                options={catalog.map((m: OrgCompanyMission) => ({ id: m.missionid, label: m.title }))} />
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 6 }}>
                <TouchableOpacity onPress={() => remove(g)}><Text style={{ color: c.error, fontSize: 13 }}>{t('company.delete')}</Text></TouchableOpacity>
                <TouchableOpacity onPress={() => save(g)} disabled={busy} style={[s.saveBtn, { backgroundColor: c.primary }]}>
                  <Text style={{ color: '#FFF', fontWeight: '700' }}>{busy ? '…' : t('common.save')}</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>
      ))}
      {groups.length === 0 && <Text style={{ color: c.textTertiary, fontSize: 13 }}>{t('companyDetail.group.none')}</Text>}
    </View>
  );
}

// --- Members tab (activation / deactivation) -------------------------------
// Same module-level trap as TABS: the map holds KEYS, resolved per render.
const MEMBER_STATUS: Record<string, MessageKey> = {
  active: 'company.status.active',
  accepted: 'company.status.pendingActivation',
  inactive: 'company.status.inactive',
  declined: 'company.status.declined',
};
function MembersTab({ c, token, companyId, members, roles, usage, reload, goBuy }: any) {
  const t = useT();
  const [busy, setBusy] = useState<number | null>(null);
  // Roles panel opened on this member. ⚠️ `roles` comes from the SERVER, which already strips
  // the CloudChrono roles outside the platform company: the list is not filtered here, otherwise
  // two places would decide the same thing and would end up diverging.
  const [ouvert, setOuvert] = useState<number | null>(null);
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [msg, setMsg] = useState<string | null>(null);
  const statusColor = (st: string) => ({ active: c.success, accepted: '#D97706', inactive: '#DC2626' } as any)[st] || c.textTertiary;

  const activate = async (m: OrgMember) => {
    if (!m.member_id) return;
    setBusy(m.member_id); setMsg(null);
    try { await orgApi.activateMember(token, companyId, m.member_id); await reload(); }
    catch (e: any) {
      if (e?.status === 402) { setMsg(t('companyDetail.member.noLicense')); goBuy(); }
      else setMsg(t('companyDetail.member.activateFailed'));
    } finally { setBusy(null); }
  };
  const deactivate = async (m: OrgMember) => {
    if (!m.member_id) return;
    setBusy(m.member_id); setMsg(null);
    try { await orgApi.deactivateMember(token, companyId, m.member_id); await reload(); }
    catch { setMsg(t('companyDetail.member.deactivateFailed')); }
    finally { setBusy(null); }
  };

  return (
    <View>
      {usage && (
        <View style={[s.usageCard, { borderColor: c.border, backgroundColor: c.cardBackground }]}>
          <Text style={{ color: c.textPrimary, fontWeight: '700' }}>
            {t('companyDetail.license.used', { used: usage.used, total: usage.licenses })}
          </Text>
          <Text style={{ color: usage.available > 0 ? c.success : c.error, fontSize: 12, marginTop: 2 }}>
            {t('companyDetail.license.available', { count: usage.available })}
            {usage.subscription_active ? '' : t('companyDetail.license.inactive')}
          </Text>
          <TouchableOpacity onPress={goBuy} style={{ marginTop: 6 }}>
            <Text style={{ color: c.primary, fontSize: 12, fontWeight: '700' }}>{t('companyDetail.license.buy')}</Text>
          </TouchableOpacity>
        </View>
      )}
      {msg && <Text style={{ color: c.textSecondary, fontSize: 12, marginBottom: 8 }}>{msg}</Text>}
      {members.map((m: OrgMember) => (
        <View key={m.app_user_id} style={[s.row, { borderColor: c.border }]}>
          <View style={{ flex: 1 }}>
            <Text style={{ color: c.textPrimary, fontSize: 13 }}>{m.email}</Text>
            <Text style={{ color: m.is_admin ? c.primary : statusColor(m.status || ''), fontSize: 11, marginTop: 2 }}>
              {m.is_admin ? t('companyDetail.member.admin')
                : (MEMBER_STATUS[m.status || ''] ? t(MEMBER_STATUS[m.status || '']) : m.status)}
            </Text>
          </View>
          {!m.is_admin && m.member_id && (m.status === 'accepted' || m.status === 'inactive') && (
            <TouchableOpacity disabled={busy === m.member_id} onPress={() => activate(m)} style={[s.miniBtn, { backgroundColor: c.primary }]}>
              <Text style={{ color: '#FFF', fontSize: 12, fontWeight: '700' }}>{busy === m.member_id ? '…' : t('companyDetail.member.activate')}</Text>
            </TouchableOpacity>
          )}
          {/* DIRECT purchase from the member's own row: when a licence is missing the admin is
              already looking at the person concerned — sending them off to the Licences tab
              loses that context, and losing context is where people give up. */}
          {!m.is_admin && m.member_id && m.status !== 'active' && (usage?.available ?? 0) <= 0 && (
            <TouchableOpacity onPress={goBuy} style={[s.miniBtnGhost, { borderColor: c.primary }]}>
              <Text style={{ color: c.primary, fontSize: 12, fontWeight: '600' }}>{t('companyDetail.member.buyLicense')}</Text>
            </TouchableOpacity>
          )}
          {!m.is_admin && m.member_id && (
            <TouchableOpacity onPress={() => {
              setOuvert(ouvert === m.member_id ? null : (m.member_id ?? null));
              setSel(new Set<number>((m.role_ids ?? []).map((x: any) => Number(x))));
            }} style={[s.miniBtnGhost, { borderColor: c.border }]}>
              <Text style={{ color: c.textSecondary, fontSize: 12, fontWeight: '600' }}>{t('companyDetail.member.roles')}</Text>
            </TouchableOpacity>
          )}
          {!m.is_admin && m.member_id && m.status === 'active' && (
            <TouchableOpacity disabled={busy === m.member_id} onPress={() => deactivate(m)} style={[s.miniBtnGhost, { borderColor: c.border }]}>
              <Text style={{ color: c.textSecondary, fontSize: 12, fontWeight: '600' }}>{busy === m.member_id ? '…' : t('companyDetail.member.deactivate')}</Text>
            </TouchableOpacity>
          )}
          {ouvert === m.member_id && (
            <View style={{ width: '100%', marginTop: 8, gap: 6 }}>
              <Text style={{ color: c.textTertiary, fontSize: 11 }}>{t('companyDetail.member.rolesHelp')}</Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                {(roles ?? []).map((r: any) => {
                  const on = sel.has(r.roleid);
                  return (
                    <TouchableOpacity key={r.roleid} onPress={() => setSel((p) => {
                      const n = new Set(p); n.has(r.roleid) ? n.delete(r.roleid) : n.add(r.roleid); return n;
                    })} style={[s.miniBtnGhost, { borderColor: on ? c.primary : c.border }]}>
                      <Text style={{ color: on ? c.primary : c.textSecondary, fontSize: 12 }}>{r.name || r.key}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
              <TouchableOpacity disabled={busy === m.member_id} style={[s.miniBtn, { backgroundColor: c.primary, alignSelf: 'flex-start' }]}
                onPress={async () => {
                  setBusy(m.member_id ?? null); setMsg('');
                  try {
                    const out = await orgApi.setMemberRoles(token, companyId, m.member_id as number, [...sel]);
                    // The server SAYS whether it applied or merely recorded: a member who is
                    // not activated has no access yet, and letting the admin believe otherwise
                    // would have them hunt for a right that only arrives at activation.
                    setMsg(t(out.applied ? 'companyDetail.member.rolesSaved' : 'companyDetail.member.rolesPending'));
                    setOuvert(null); await reload();
                  } catch { setMsg(t('companyDetail.member.rolesFailed')); }
                  finally { setBusy(null); }
                }}>
                <Text style={{ color: '#FFF', fontSize: 12, fontWeight: '700' }}>{busy === m.member_id ? '…' : t('common.save')}</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      ))}
      {members.length === 0 && <Text style={{ color: c.textTertiary, fontSize: 13 }}>{t('companyDetail.member.none')}</Text>}
    </View>
  );
}

// --- Invitations tab -------------------------------------------------------
function InvitationsTab({ c, token, companyId, roles, groups, invitations, reload }: any) {
  const t = useT();
  const [email, setEmail] = useState('');
  const [rSel, setRSel] = useState<Set<number>>(new Set());
  const [gSel, setGSel] = useState<Set<number>>(new Set());
  const [reqAccept, setReqAccept] = useState(false);   // default: informative invitation (no acceptance)
  const [msg, setMsg] = useState<string | null>(null);
  const toggle = (set: any, val: number) => set((p: Set<number>) => { const n = new Set(p); n.has(val) ? n.delete(val) : n.add(val); return n; });

  const invite = async () => {
    if (!email.includes('@')) { setMsg(t('companyDetail.invite.badEmail')); return; }
    try {
      await orgApi.invite(token, companyId, { email: email.trim(), role_ids: [...rSel], group_ids: [...gSel], requires_acceptance: reqAccept });
      setEmail(''); setRSel(new Set()); setGSel(new Set());
      setMsg(t(reqAccept ? 'companyDetail.invite.sentPending' : 'companyDetail.invite.added')); await reload();
    } catch { setMsg(t('companyDetail.invite.sendFailed')); }
  };

  const [busyId, setBusyId] = useState<number | null>(null);
  const resend = async (iid: number) => {
    setBusyId(iid); setMsg(null);
    try { await orgApi.resendInvitation(token, companyId, iid); setMsg(t('companyDetail.invite.resent')); await reload(); }
    catch { setMsg(t('companyDetail.invite.resendFailed')); }
    finally { setBusyId(null); }
  };
  const cancel = async (iid: number) => {
    setBusyId(iid); setMsg(null);
    try { await orgApi.cancelInvitation(token, companyId, iid); setMsg(t('companyDetail.invite.cancelled')); await reload(); }
    catch { setMsg(t('companyDetail.invite.cancelFailed')); }
    finally { setBusyId(null); }
  };

  return (
    <View>
      <Text style={[s.lbl, { color: c.textSecondary }]}>{t('companyDetail.invite.title')}</Text>
      <TextInput placeholder="email@exemple.com" value={email} onChangeText={setEmail} autoCapitalize="none" keyboardType="email-address"
        placeholderTextColor={c.inputPlaceholder}
        style={[s.input, { backgroundColor: c.inputBackground, borderColor: c.inputBorder, color: c.inputText }]} />
      <Text style={[s.lbl, { color: c.textSecondary }]}>{t('companyDetail.invite.rolesOnAccept')}</Text>
      <Chips c={c} selected={rSel} onToggle={(id) => toggle(setRSel, id)} options={roles.map((r: OrgRole) => ({ id: r.roleid, label: r.name }))} />
      <Text style={[s.lbl, { color: c.textSecondary }]}>{t('companyDetail.invite.groups')}</Text>
      <Chips c={c} selected={gSel} onToggle={(id) => toggle(setGSel, id)} options={groups.map((g: OrgGroup) => ({ id: g.group_id, label: g.name }))} />
      {/* Invitation mode: informative by default (no acceptance). Checked = acceptance required. */}
      <TouchableOpacity onPress={() => setReqAccept(v => !v)} accessibilityRole="checkbox" accessibilityState={{ checked: reqAccept }}
        style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 12 }}>
        <View style={{ width: 20, height: 20, borderRadius: 5, borderWidth: 2, borderColor: reqAccept ? c.primary : c.inputBorder,
          backgroundColor: reqAccept ? c.primary : 'transparent', alignItems: 'center', justifyContent: 'center' }}>
          {reqAccept && <Text style={{ color: '#FFF', fontSize: 13, fontWeight: '900' }}>✓</Text>}
        </View>
        <Text style={{ flex: 1, color: c.textPrimary, fontSize: 13 }}>
          {t('companyDetail.invite.requiresAcceptance')}{'\n'}
          <Text style={{ color: c.textTertiary, fontSize: 12 }}>
            {t(reqAccept ? 'companyDetail.invite.requiresAcceptanceOn' : 'companyDetail.invite.requiresAcceptanceOff')}
          </Text>
        </Text>
      </TouchableOpacity>
      {msg && <Text style={{ color: c.textSecondary, fontSize: 12, marginTop: 8 }}>{msg}</Text>}
      <TouchableOpacity onPress={invite} style={[s.fullBtn, { backgroundColor: c.primary }]}>
        <Text style={{ color: '#FFF', fontWeight: '700' }}>
          {t(reqAccept ? 'companyDetail.invite.submitWithAcceptance' : 'companyDetail.invite.submitDirect')}
        </Text>
      </TouchableOpacity>

      <Text style={[s.lbl, { color: c.textSecondary, marginTop: 18 }]}>{t('companyDetail.invite.sent')}</Text>
      {invitations.map((i: OrgInvitation) => (
        <View key={i.invitation_id} style={[s.row, { borderColor: c.border }]}>
          <Text style={{ color: c.textPrimary, fontSize: 13, flex: 1 }}>{i.email}</Text>
          {i.status === 'pending' ? (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              <TouchableOpacity disabled={busyId === i.invitation_id} onPress={() => resend(i.invitation_id)}>
                <Text style={{ color: c.primary, fontSize: 12, fontWeight: '600' }}>{t('companyDetail.invite.resend')}</Text>
              </TouchableOpacity>
              <TouchableOpacity disabled={busyId === i.invitation_id} onPress={() => cancel(i.invitation_id)}>
                <Text style={{ color: c.danger ?? '#ef4444', fontSize: 12, fontWeight: '600' }}>{t('common.cancel')}</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <Text style={{ color: i.status === 'accepted' ? c.success : c.textTertiary, fontSize: 12 }}>{i.status}</Text>
          )}
        </View>
      ))}
      {invitations.length === 0 && <Text style={{ color: c.textTertiary, fontSize: 13 }}>{t('companyDetail.invite.none')}</Text>}
    </View>
  );
}

// --- Subscription tab (licenses + purchase through the cart) ---------------
function SubscriptionTab({ c, token, companyId, subscription, usage, onSaved, onUsage }: any) {
  const t = useT();
  const [plan, setPlan] = useState(subscription?.plan ?? 'business');
  const [qty, setQty] = useState('1');
  const [cart, setCart] = useState<LicenseCart | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPlan(subscription?.plan ?? 'business'); }, [subscription]);
  useEffect(() => { orgApi.licenseCart(token, companyId).then(setCart).catch(() => {}); }, [token, companyId]);

  const savePlan = async () => {
    const r: any = await orgApi.setSubscription(token, companyId, { plan, licenses: usage?.licenses ?? subscription?.licenses ?? 0 });
    onSaved(r, null); setMsg(t('companyDetail.sub.planSaved'));
  };
  const buy = async () => {
    const n = Math.max(1, Number(qty) || 0);
    setBusy(true); setMsg(null);
    try {
      await orgApi.setLicenseCart(token, companyId, n);
      const r = await orgApi.checkoutLicenses(token, companyId);
      if (r.checkout_url) {           // provider checkout: redirect
        if (Platform.OS === 'web' && typeof window !== 'undefined') { window.location.href = r.checkout_url; return; }
        setMsg(t('companyDetail.sub.paymentLink')); return;
      }
      if (r.usage) onUsage(r.usage);  // free / simulated: direct order
      setMsg(t('companyDetail.sub.added', { count: n }));
      orgApi.licenseCart(token, companyId).then(setCart).catch(() => {});
    } catch { setMsg(t('companyDetail.sub.buyFailed')); }
    finally { setBusy(false); }
  };

  const unit = cart?.unit_price ?? 0;
  const n = Math.max(1, Number(qty) || 0);
  const total = (unit * n).toFixed(2);

  return (
    <View>
      {usage && (
        <View style={[s.usageCard, { borderColor: c.border, backgroundColor: c.cardBackground }]}>
          <Text style={{ color: c.textPrimary, fontWeight: '700' }}>
            {t('companyDetail.license.used', { used: usage.used, total: usage.licenses })}
          </Text>
          <Text style={{ color: usage.available > 0 ? c.success : c.textSecondary, fontSize: 12, marginTop: 2 }}>
            {t('companyDetail.license.available', { count: usage.available })}
            {usage.subscription_active ? '' : t('companyDetail.license.inactive')}
          </Text>
        </View>
      )}
      <Text style={[s.lbl, { color: c.textSecondary }]}>{t('companyDetail.sub.plan')}</Text>
      <View style={s.chipsRow}>
        {['business', 'enterprise'].map(p => (
          <TouchableOpacity key={p} onPress={() => setPlan(p)}
            style={[s.chip, { borderColor: plan === p ? c.primary : c.border, backgroundColor: plan === p ? c.primary + '22' : 'transparent' }]}>
            <Text style={{ color: c.textPrimary, fontSize: 12 }}>{p}</Text>
          </TouchableOpacity>
        ))}
        <TouchableOpacity onPress={savePlan} style={[s.miniBtnGhost, { borderColor: c.border }]}>
          <Text style={{ color: c.textSecondary, fontSize: 12, fontWeight: '600' }}>{t('companyDetail.sub.savePlan')}</Text>
        </TouchableOpacity>
      </View>

      <Text style={[s.lbl, { color: c.textSecondary, marginTop: 18 }]}>{t('companyDetail.sub.buy')}</Text>
      <Text style={{ color: c.textTertiary, fontSize: 12, marginBottom: 8 }}>
        {t('companyDetail.sub.unitPrice', { price: unit === 0 ? t('companyDetail.sub.free') : `${unit.toFixed(2)} €` })}
      </Text>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <TextInput value={qty} onChangeText={setQty} keyboardType="number-pad" placeholderTextColor={c.inputPlaceholder}
          style={[s.input, { width: 80, backgroundColor: c.inputBackground, borderColor: c.inputBorder, color: c.inputText }]} />
        <Text style={{ color: c.textSecondary, fontSize: 13 }}>× {unit.toFixed(2)} € = <Text style={{ color: c.textPrimary, fontWeight: '700' }}>{total} €</Text></Text>
      </View>
      <TouchableOpacity onPress={buy} disabled={busy} style={[s.fullBtn, { backgroundColor: c.primary }]}>
        <Text style={{ color: '#FFF', fontWeight: '700' }}>
          {busy ? '…'
            : unit === 0 ? t('companyDetail.sub.get', { count: n })
            : t('companyDetail.sub.pay', { total: `${total} €` })}
        </Text>
      </TouchableOpacity>
      {msg && <Text style={{ color: c.success, fontSize: 12, marginTop: 8 }}>{msg}</Text>}
      <Text style={{ color: c.textTertiary, fontSize: 12, marginTop: 10 }}>
        {t('companyDetail.sub.note')}
      </Text>
    </View>
  );
}

// (The Catalogue tab was removed: a mission's availability follows from its OWNERSHIP
//  — mission.company_id — and from its publication (is_public → marketplace), both managed in the BO.)

const s = StyleSheet.create({
  container: { flex: 1 },
  tabs: { flexDirection: 'row', margin: 12, borderRadius: 8, padding: 4 },
  tab: { flex: 1, paddingVertical: 8, borderRadius: 6, alignItems: 'center' },
  scroll: { padding: 16, paddingBottom: 40 },
  inlineForm: { flexDirection: 'row', gap: 8, marginBottom: 12 },
  input: { height: 40, borderRadius: 8, borderWidth: 1, paddingHorizontal: 10, fontSize: 14 },
  addBtn: { width: 40, height: 40, borderRadius: 8, alignItems: 'center', justifyContent: 'center' },
  card: { borderRadius: 12, padding: 14, marginBottom: 10 },
  cardHead: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  cardTitle: { fontSize: 15, fontWeight: '700' },
  lbl: { fontSize: 12, fontWeight: '700', marginBottom: 6, marginTop: 8 },
  chipsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  chip: { paddingHorizontal: 10, paddingVertical: 6, borderRadius: 999, borderWidth: 1 },
  saveBtn: { paddingHorizontal: 16, paddingVertical: 9, borderRadius: 8 },
  fullBtn: { marginTop: 14, paddingVertical: 12, borderRadius: 10, alignItems: 'center' },
  row: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 10, borderBottomWidth: 1 },
  catRow: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, borderRadius: 10, borderWidth: 1 },
  usageCard: { borderWidth: 1, borderRadius: 10, padding: 12, marginBottom: 14 },
  miniBtn: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 8 },
  miniBtnGhost: { paddingHorizontal: 14, paddingVertical: 7, borderRadius: 8, borderWidth: 1 },
});
